import React, { useState, useEffect, useCallback, useRef } from "react";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import IconButton from "@material-ui/core/IconButton";
import "./ChildrenPaintingAnimation.css";

const AUTO_PLAY_MS = 5500;

const SLIDES = [
  {
    id: "painting",
    tag: "Creative learning",
    title: "Paint, explore, and create",
    subtitle: "Children express ideas through color, texture, and joyful imagination in every classroom.",
    colors: ["#dbeafe", "#e0f2fe", "#fef9c3"],
    hoverColors: ["#93c5fd", "#bae6fd", "#fde047"],
    accent: "#1769d6",
    accentSoft: "#eef4ff",
    glow: "rgba(23, 105, 214, 0.28)",
    scene: "painting",
  },
  {
    id: "blocks",
    tag: "Hands-on materials",
    title: "Build, balance, and discover",
    subtitle: "Montessori materials help children develop focus, order, and confidence through touch.",
    colors: ["#ede9fe", "#f3e8ff", "#e0e7ff"],
    hoverColors: ["#c4b5fd", "#d8b4fe", "#a5b4fc"],
    accent: "#7c4dff",
    accentSoft: "#f3efff",
    glow: "rgba(124, 77, 255, 0.28)",
    scene: "blocks",
  },
  {
    id: "reading",
    tag: "Language & literacy",
    title: "Read, listen, and grow",
    subtitle: "Story time and language work nurture curiosity and communication from an early age.",
    colors: ["#dcfce7", "#ecfdf5", "#e0f2fe"],
    hoverColors: ["#86efac", "#a7f3d0", "#7dd3fc"],
    accent: "#2e7d32",
    accentSoft: "#e8f5e9",
    glow: "rgba(46, 125, 50, 0.28)",
    scene: "reading",
  },
  {
    id: "nature",
    tag: "Nature & care",
    title: "Explore the world outside",
    subtitle: "Gardening and outdoor activities connect children with nature and responsible care.",
    colors: ["#fef3c7", "#ecfccb", "#d9f99d"],
    hoverColors: ["#fcd34d", "#bef264", "#a3e635"],
    accent: "#65a30d",
    accentSoft: "#f7fee7",
    glow: "rgba(101, 163, 13, 0.28)",
    scene: "nature",
  },
  {
    id: "music",
    tag: "Music & movement",
    title: "Rhythm, song, and movement",
    subtitle: "Music activities support coordination, expression, and joyful group learning.",
    colors: ["#fce7f3", "#fdf2f8", "#ffedd5"],
    hoverColors: ["#f9a8d4", "#fbcfe8", "#fdba74"],
    accent: "#db2777",
    accentSoft: "#fff0f6",
    glow: "rgba(219, 39, 119, 0.28)",
    scene: "music",
  },
];

const PaintingScene = ({ uid }) => (
  <svg viewBox="0 0 900 240" className="home-carousel-svg" aria-hidden>
    <defs>
      <linearGradient id={`sky-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#eef4ff" />
        <stop offset="100%" stopColor="#fff8ee" />
      </linearGradient>
    </defs>
    <rect width="900" height="240" fill={`url(#sky-${uid})`} />
    <ellipse cx="450" cy="222" rx="380" ry="14" fill="rgba(15,45,77,0.06)" />
    <g className="home-painting-easel">
      <path d="M380 200 L430 90 L480 200 Z" fill="#c4a574" stroke="#a08050" strokeWidth="2" />
      <rect x="396" y="100" width="68" height="88" rx="5" fill="#fff" stroke="#d0d8e4" strokeWidth="2" />
      <path className="home-stroke home-stroke--1" d="M408 118 Q435 98 462 118 T516 108" fill="none" stroke="#1769d6" strokeWidth="5" strokeLinecap="round" />
      <path className="home-stroke home-stroke--2" d="M404 142 Q430 152 456 138 T508 148" fill="none" stroke="#f5a623" strokeWidth="5" strokeLinecap="round" />
      <path className="home-stroke home-stroke--3" d="M410 168 Q440 178 468 162 T520 172" fill="none" stroke="#2e7d32" strokeWidth="5" strokeLinecap="round" />
      <circle className="home-stroke home-stroke--4" cx="490" cy="125" r="11" fill="#e91e8c" opacity="0.75" />
    </g>
    <g className="home-painting-child home-painting-child--1">
      <ellipse cx="330" cy="198" rx="28" ry="10" fill="rgba(15,45,77,0.08)" />
      <circle cx="330" cy="138" r="20" fill="#ffd4b8" />
      <path d="M310 138 Q305 115 325 110 Q345 115 340 138" fill="#5c4033" />
      <rect x="316" y="156" width="28" height="36" rx="9" fill="#1769d6" />
      <path d="M344 164 L370 118" stroke="#ffd4b8" strokeWidth="9" strokeLinecap="round" />
      <g className="home-painting-brush">
        <line x1="370" y1="118" x2="398" y2="108" stroke="#8b6914" strokeWidth="4" strokeLinecap="round" />
        <path d="M394 104 L404 100 L400 114 Z" fill="#2e7d32" />
      </g>
    </g>
    <g className="home-painting-child home-painting-child--2">
      <ellipse cx="540" cy="200" rx="24" ry="9" fill="rgba(15,45,77,0.08)" />
      <circle cx="540" cy="145" r="17" fill="#f5d0b8" />
      <rect x="528" y="160" width="24" height="32" rx="7" fill="#7c4dff" />
      <ellipse cx="572" cy="158" rx="18" ry="13" fill="#f5f0e8" stroke="#c4b8a8" strokeWidth="1.5" />
      <circle cx="564" cy="154" r="4" fill="#1769d6" />
      <circle cx="574" cy="158" r="4" fill="#f5a623" />
      <circle cx="568" cy="166" r="4" fill="#2e7d32" />
    </g>
    <g className="home-painting-child home-painting-child--3">
      <ellipse cx="640" cy="202" rx="20" ry="8" fill="rgba(15,45,77,0.08)" />
      <circle cx="640" cy="158" r="14" fill="#ffe0c8" />
      <rect x="632" y="170" width="16" height="28" rx="5" fill="#2e7d32" />
    </g>
    <circle className="home-splash home-splash--1" cx="220" cy="80" r="8" fill="#1769d6" opacity="0.35" />
    <circle className="home-splash home-splash--2" cx="700" cy="60" r="7" fill="#f5a623" opacity="0.4" />
    <g className="home-sparkle home-sparkle--1" fill="#f5a623">
      <path d="M250 55 l3 8 8 3 -8 3 -3 8 -3-8 -8-3 8-3z" />
    </g>
  </svg>
);

const BlocksScene = ({ uid }) => (
  <svg viewBox="0 0 900 240" className="home-carousel-svg" aria-hidden>
    <defs>
      <linearGradient id={`blk-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f3efff" />
        <stop offset="100%" stopColor="#eef4ff" />
      </linearGradient>
    </defs>
    <rect width="900" height="240" fill={`url(#blk-${uid})`} />
    <ellipse cx="450" cy="222" rx="380" ry="14" fill="rgba(15,45,77,0.06)" />
    <g className="home-painting-child home-painting-child--1">
      <ellipse cx="320" cy="200" rx="26" ry="9" fill="rgba(15,45,77,0.08)" />
      <circle cx="320" cy="142" r="19" fill="#ffd4b8" />
      <rect x="304" y="158" width="32" height="38" rx="8" fill="#1769d6" />
    </g>
    <g className="home-float-block">
      <rect x="400" y="168" width="48" height="48" rx="6" fill="#f5a623" />
      <rect x="400" y="120" width="48" height="48" rx="6" fill="#e91e8c" />
      <rect x="400" y="72" width="48" height="48" rx="6" fill="#1769d6" />
      <rect x="456" y="144" width="48" height="48" rx="6" fill="#2e7d32" />
      <rect x="456" y="96" width="48" height="48" rx="6" fill="#7c4dff" />
    </g>
    <g className="home-painting-child home-painting-child--2">
      <ellipse cx="580" cy="200" rx="22" ry="8" fill="rgba(15,45,77,0.08)" />
      <circle cx="580" cy="150" r="16" fill="#f5d0b8" />
      <rect x="568" y="164" width="24" height="34" rx="7" fill="#2e7d32" />
      <path d="M556 158 L540 175" stroke="#ffd4b8" strokeWidth="8" strokeLinecap="round" />
    </g>
    <circle className="home-splash home-splash--3" cx="200" cy="100" r="10" fill="#7c4dff" opacity="0.3" />
    <circle className="home-splash home-splash--4" cx="720" cy="90" r="8" fill="#f5a623" opacity="0.35" />
  </svg>
);

const ReadingScene = ({ uid }) => (
  <svg viewBox="0 0 900 240" className="home-carousel-svg" aria-hidden>
    <defs>
      <linearGradient id={`read-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#e8f5e9" />
        <stop offset="100%" stopColor="#eef4ff" />
      </linearGradient>
    </defs>
    <rect width="900" height="240" fill={`url(#read-${uid})`} />
    <ellipse cx="450" cy="222" rx="380" ry="14" fill="rgba(15,45,77,0.06)" />
    <rect x="360" y="175" width="180" height="12" rx="4" fill="#c4a574" />
    <rect x="370" y="120" width="24" height="58" rx="3" fill="#8b6914" />
    <rect x="506" y="120" width="24" height="58" rx="3" fill="#8b6914" />
    <rect x="388" y="128" width="124" height="50" rx="4" fill="#fff" stroke="#d0d8e4" strokeWidth="2" />
    <path className="home-stroke home-stroke--1" d="M400 145 h100" stroke="#1769d6" strokeWidth="3" strokeLinecap="round" />
    <path className="home-stroke home-stroke--2" d="M400 158 h80" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    <path className="home-stroke home-stroke--3" d="M400 168 h90" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    <g className="home-painting-child home-painting-child--1">
      <ellipse cx="300" cy="200" rx="24" ry="8" fill="rgba(15,45,77,0.08)" />
      <circle cx="300" cy="148" r="17" fill="#ffd4b8" />
      <rect x="286" y="162" width="28" height="36" rx="8" fill="#7c4dff" />
      <path d="M318 170 L360 155" stroke="#ffd4b8" strokeWidth="8" strokeLinecap="round" />
    </g>
    <g className="home-painting-child home-painting-child--3">
      <ellipse cx="600" cy="202" rx="18" ry="7" fill="rgba(15,45,77,0.08)" />
      <circle cx="600" cy="160" r="13" fill="#ffe0c8" />
      <rect x="592" y="172" width="16" height="28" rx="5" fill="#1769d6" />
    </g>
    <g className="home-sparkle home-sparkle--2" fill="#2e7d32">
      <path d="M680 70 l2 6 6 2 -6 2 -2 6 -2-6 -6-2 6-2z" />
    </g>
  </svg>
);

const NatureScene = ({ uid }) => (
  <svg viewBox="0 0 900 240" className="home-carousel-svg" aria-hidden>
    <defs>
      <linearGradient id={`nat-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fff8ee" />
        <stop offset="100%" stopColor="#e8f5e9" />
      </linearGradient>
    </defs>
    <rect width="900" height="240" fill={`url(#nat-${uid})`} />
    <ellipse cx="450" cy="222" rx="380" ry="14" fill="rgba(15,45,77,0.06)" />
    <ellipse cx="450" cy="195" rx="120" ry="22" fill="#8d6e63" opacity="0.35" />
    <path d="M450 80 Q420 140 450 195 Q480 140 450 80" fill="#2e7d32" opacity="0.85" />
    <path d="M450 100 Q400 150 430 190" fill="none" stroke="#1b5e20" strokeWidth="3" />
    <path d="M450 100 Q500 150 470 190" fill="none" stroke="#1b5e20" strokeWidth="3" />
    <circle cx="430" cy="115" r="12" fill="#f5a623" opacity="0.8" className="home-splash home-splash--1" />
    <circle cx="472" cy="108" r="10" fill="#e91e8c" opacity="0.7" className="home-splash home-splash--2" />
    <g className="home-painting-child home-painting-child--1">
      <ellipse cx="340" cy="200" rx="24" ry="8" fill="rgba(15,45,77,0.08)" />
      <circle cx="340" cy="148" r="17" fill="#ffd4b8" />
      <rect x="326" y="162" width="28" height="36" rx="8" fill="#2e7d32" />
      <path d="M354 172 L390 185" stroke="#ffd4b8" strokeWidth="8" strokeLinecap="round" />
    </g>
    <g className="home-painting-child home-painting-child--2">
      <ellipse cx="560" cy="200" rx="22" ry="8" fill="rgba(15,45,77,0.08)" />
      <circle cx="560" cy="150" r="16" fill="#f5d0b8" />
      <rect x="548" y="164" width="24" height="34" rx="7" fill="#1769d6" />
    </g>
    <circle className="home-splash home-splash--3" cx="200" cy="70" r="9" fill="#2e7d32" opacity="0.35" />
  </svg>
);

const MusicScene = ({ uid }) => (
  <svg viewBox="0 0 900 240" className="home-carousel-svg" aria-hidden>
    <defs>
      <linearGradient id={`mus-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#eef4ff" />
        <stop offset="100%" stopColor="#fff0f6" />
      </linearGradient>
    </defs>
    <rect width="900" height="240" fill={`url(#mus-${uid})`} />
    <ellipse cx="450" cy="222" rx="380" ry="14" fill="rgba(15,45,77,0.06)" />
    <ellipse cx="450" cy="175" rx="55" ry="38" fill="#f5a623" stroke="#d48806" strokeWidth="2" />
    <ellipse cx="450" cy="175" rx="18" ry="12" fill="#fff8ee" opacity="0.6" />
    <g className="home-music-note home-splash--1">
      <ellipse cx="520" cy="100" rx="8" ry="6" fill="#1769d6" />
      <rect x="526" y="60" width="3" height="42" fill="#1769d6" />
    </g>
    <g className="home-music-note home-splash--2">
      <ellipse cx="380" cy="90" rx="7" ry="5" fill="#e91e8c" />
      <rect x="385" y="55" width="3" height="38" fill="#e91e8c" />
    </g>
    <g className="home-painting-child home-painting-child--1">
      <ellipse cx="340" cy="200" rx="26" ry="9" fill="rgba(15,45,77,0.08)" />
      <circle cx="340" cy="142" r="19" fill="#ffd4b8" />
      <rect x="324" y="158" width="32" height="38" rx="8" fill="#e91e8c" />
      <path d="M358 168 L400 155" stroke="#ffd4b8" strokeWidth="9" strokeLinecap="round" />
    </g>
    <g className="home-painting-child home-painting-child--2">
      <ellipse cx="560" cy="200" rx="22" ry="8" fill="rgba(15,45,77,0.08)" />
      <circle cx="560" cy="150" r="16" fill="#f5d0b8" />
      <rect x="548" y="164" width="24" height="34" rx="7" fill="#7c4dff" />
    </g>
    <g className="home-sparkle home-sparkle--1" fill="#f5a623">
      <path d="M650 65 l2 7 7 2 -7 2 -2 7 -2-7 -7-2 7-2z" />
    </g>
  </svg>
);

const SCENE_MAP = {
  painting: PaintingScene,
  blocks: BlocksScene,
  reading: ReadingScene,
  nature: NatureScene,
  music: MusicScene,
};

const ChildrenPaintingAnimation = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef(null);
  const currentSlide = SLIDES[active];

  const goTo = useCallback((index) => {
    const next = (index + SLIDES.length) % SLIDES.length;
    setActive(next);
  }, []);

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = setInterval(() => {
      setActive((i) => (i + 1) % SLIDES.length);
    }, AUTO_PLAY_MS);
    return () => clearInterval(timerRef.current);
  }, [paused]);

  const carouselStyle = {
    "--carousel-accent": currentSlide.accent,
    "--carousel-accent-soft": currentSlide.accentSoft,
    "--carousel-glow": currentSlide.glow,
  };

  return (
    <section
      className={`home-carousel home-carousel--${currentSlide.id}${hovered ? " is-hovered" : ""}`}
      style={carouselStyle}
      aria-roledescription="carousel"
      aria-label="Montessori learning highlights"
      onMouseEnter={() => {
        setPaused(true);
        setHovered(true);
      }}
      onMouseLeave={() => {
        setPaused(false);
        setHovered(false);
      }}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="home-carousel__header">
        <span className="home-carousel__eyebrow">Learning in action</span>
        <div className="home-carousel__counter">
          {String(active + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
        </div>
      </div>

      <div className="home-carousel__frame">
        <IconButton
          className="home-carousel__arrow home-carousel__arrow--prev"
          onClick={prev}
          aria-label="Previous slide"
        >
          <ChevronLeftIcon />
        </IconButton>

        <div className="home-carousel__viewport">
          <div
            className="home-carousel__track"
            style={{ transform: `translateX(-${active * 100}%)` }}
          >
            {SLIDES.map((slide, index) => {
              const Scene = SCENE_MAP[slide.scene];
              const isActive = index === active;
              const slideStyle = {
                "--slide-c1": slide.colors[0],
                "--slide-c2": slide.colors[1],
                "--slide-c3": slide.colors[2],
                "--slide-h1": slide.hoverColors[0],
                "--slide-h2": slide.hoverColors[1],
                "--slide-h3": slide.hoverColors[2],
                "--slide-accent": slide.accent,
                "--slide-accent-soft": slide.accentSoft,
                "--slide-glow": slide.glow,
              };
              return (
                <article
                  key={slide.id}
                  className={`home-carousel-slide home-carousel-slide--${slide.id}${isActive ? " is-active" : ""}`}
                  style={slideStyle}
                  aria-hidden={!isActive}
                  aria-label={`${slide.title}. ${slide.subtitle}`}
                >
                  <div className="home-carousel-slide__gradient" />
                  <div className="home-carousel-slide__gradient-hover" />
                  <div className="home-carousel-slide__orb home-carousel-slide__orb--1" />
                  <div className="home-carousel-slide__orb home-carousel-slide__orb--2" />
                  <div className="home-carousel-slide__content">
                    <span className="home-carousel-slide__tag">{slide.tag}</span>
                    <h2 className="home-carousel-slide__title">{slide.title}</h2>
                    <p className="home-carousel-slide__subtitle">{slide.subtitle}</p>
                  </div>
                  <div className="home-carousel-slide__scene">
                    <Scene uid={`${slide.id}-${index}`} />
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <IconButton
          className="home-carousel__arrow home-carousel__arrow--next"
          onClick={next}
          aria-label="Next slide"
        >
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className="home-carousel__dots" role="tablist" aria-label="Choose slide">
        {SLIDES.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            role="tab"
            aria-selected={index === active}
            aria-label={slide.title}
            className={`home-carousel__dot home-carousel__dot--${slide.id}${index === active ? " is-active" : ""}`}
            onClick={() => goTo(index)}
          />
        ))}
      </div>
    </section>
  );
};

export default ChildrenPaintingAnimation;
